/**
 * MobileLayout — smartphones in a browser (Chrome/Safari/Firefox).
 *
 * Three distinct browser environments handled:
 *   chrome-android  hasBrowserChrome=true   ~56px addr bar + ~48px nav bar (persistent)
 *   safari-ios      hasBrowserChrome=false  bar auto-hides on scroll; safe-area-inset-bottom handles home indicator
 *   chrome-ios      hasBrowserChrome=false  runs WebKit same as Safari
 *   firefox-android hasBrowserChrome=true   same persistent UI as Chrome Android
 *
 * Performance contract — zero exceptions:
 *   ✗ NO backdrop-filter/blur — kills GPU compositing on mid-range phones
 *   ✗ NO box-shadow on scroll containers
 *   ✗ NO CSS transitions on background-color during scroll
 *   ✗ NO will-change on scroll containers (extra GPU layers, memory pressure)
 *   ✓ Visual Viewport API → --mobile-h updated live; 100dvh fallback for older browsers
 *   ✓ hasBrowserChrome: extra bottom padding when Chrome/Firefox nav bar is present
 *   ✓ touch-action: manipulation eliminates 300ms tap delay everywhere
 *   ✓ overscroll-behavior: contain on every scroll pane
 *   ✓ contain: layout style paint isolates fixed bars from layout recalc
 *   ✓ translateZ(0) on fixed bars → own GPU layer, decoupled from scroll layer
 *   ✓ Opacity-only touch feedback (no transform → no layout/paint during touch)
 *   ✓ Passive event listeners everywhere
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Calendar, MessageSquare,
  Bell, User, Users, BarChart2, Settings, Receipt,
  ChevronRight, ChevronLeft, ChevronDown, Menu, X, LogOut, Shield, WifiOff, Layers, Plus, DollarSign, Trophy, Radio, Globe,
} from 'lucide-react';
import { PRESS_BOX, pressBoxVisible, pressBoxNavItems } from '../constants/pressBox.js';
import { TEAM_SETTINGS_SUBSECTIONS } from './teamSettingsSubNav.js';
import { pathForTeamSwitch } from './teamSwitchNav.js';
import { useAuth } from '../context/AuthContext.jsx';
import { usePWAContext } from '../context/PWAContext.jsx';
import { chatSocket } from '../services/socket.js';
import api from '../services/api.js';
// v2.5.124 — Phase 11 League Module.
import { useLeagueModeNav } from '../hooks/useLeagueModeNav.js';
import { useCurrentSeason } from '../hooks/useCurrentSeason.js';
import { AppBanner } from '../components/shared/AppBanner.jsx';
import { PWABanners } from '../components/shared/PWABanners.jsx';
import { BottomNav } from '../components/shared/BottomNav.jsx';
import VersionTag from '../components/shared/VersionTag.jsx';
import { iconForNotifType } from '../utils/notifIcons.js';
import { pickActiveTeam, saveLastTeamId } from '../utils/lastTeam.js';
import OnboardingFlow from '../components/OnboardingFlow.jsx';

// ─── Solid surface tokens — never rgba+blur ────────────────────────
// S uses CSS custom properties so all three themes work automatically
const S = {
  bg:        'var(--bg)',
  surface:   'var(--surface)',
  topBar:    'var(--topbar-bg)',
  bottomBar: 'var(--bottombar-bg)',
  border:    'var(--border)',
  text:      'var(--text)',
  text2:     'var(--text-2)',
};

// ─── Universal touch-target style ─────────────────────────────────
const T = {
  WebkitTapHighlightColor: 'transparent',
  touchAction: 'manipulation',
  userSelect: 'none',
  WebkitUserSelect: 'none',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  outline: 'none',
};

// ─── Slide-in drawer ──────────────────────────────────────────────
function MobileDrawer({ open, onClose, teams, activeTeam, setActiveTeam, user, onLogout, onNotifsClick, unreadNotifs, currentSeasonId, currentSeasonName }) {
  const location  = useLocation();
  const navigate  = useNavigate();
  const teamColor = activeTeam?.settings?.team_color || 'var(--ice)';
  const prevPath  = useRef(location.pathname);

  useEffect(() => {
    if (open && location.pathname !== prevPath.current) {
      prevPath.current = location.pathname;
      onClose();
    } else {
      prevPath.current = location.pathname;
    }
  }, [location.pathname, open, onClose]);

  // Apr 2026 — split nav by role.
  // Players get only Dashboard, Schedule, Chat, Team. Players page,
  // Seasons, Stats, and Notification Log are coach/manager-only.
  const isCoachish = activeTeam && ['coach','manager'].includes(activeTeam.member_role);

  // 2026-10-18 — a Press Box team gets its own slim menu (games, roster,
  // site & settings) instead of the full team nav.
  const navItems = activeTeam?.team_type === 'press_box'
    ? pressBoxNavItems(activeTeam, { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : activeTeam ? [
    { to: '/dashboard',                     icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: `/team/${activeTeam.id}/games`,   icon: Calendar,        label: 'Schedule' },
    // Apr 30 2026 — Scoreboards. Visible to all roster members
    // (everyone reads scoreboards even if they can't edit). Hidden
    // when team setting scoreboard_enabled === false.
    ...(activeTeam.settings?.scoreboard_enabled !== false ? [
      { to: `/team/${activeTeam.id}/scoreboards`, icon: Trophy, label: 'Scoreboards' },
    ] : []),
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/players`, icon: Users,           label: 'Players' },
    ] : []),
    { to: `/team/${activeTeam.id}/chat`,    icon: MessageSquare,   label: 'Chat' },
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/seasons`, icon: Layers,          label: 'Seasons' },
      { to: `/team/${activeTeam.id}/stats`,   icon: BarChart2,       label: 'Stats' },
      // Apr 2026 (Tom_Notes E2) — Finance tab.
      { to: `/team/${activeTeam.id}/finance`, icon: DollarSign,      label: 'Finance' },
      // Budget is OFF by default — only shown when a super admin has
      // enabled it for this team (teams.settings.budget_enabled).
      ...(activeTeam.settings?.budget_enabled === true ? [
        { to: `/team/${activeTeam.id}/budget`,  icon: DollarSign,      label: 'Budget' },
      ] : []),
      // Coach-only: notification dispatch history. Hidden for regular
      // players since the backend 403s them anyway — no point offering
      // a nav link that dead-ends.
      { to: `/team/${activeTeam.id}/notification-log`, icon: Bell, label: 'Notification Log' },
    ] : [
      // May 4 2026 (v2.5.70) — player-scoped Stats. See DesktopLayout
      // for full rationale.
      { to: `/team/${activeTeam.id}/my-stats`, icon: BarChart2, label: 'Stats' },
    ]),
    // May 4 2026 (v2.5.70) — Team Settings entry hidden entirely for
    // non-coach players per coach feedback. Settings sub-pages were
    // already coach-gated server-side; the only player-facing flow
    // there (Leave Team) has been moved to the Profile page.
    ...(isCoachish ? [
      { to: `/team/${activeTeam.id}/settings`, icon: Settings,
        label: 'Team',
        // Apr 2026 — expandable sub-nav (coach/manager only). On mobile
        // the drawer auto-closes after route change, but reopening the
        // drawer (☰) shows it expanded since expansion is derived from
        // the URL — coaches re-open the drawer to switch sub-pages.
        children: TEAM_SETTINGS_SUBSECTIONS.map(s => ({
          to: `/team/${activeTeam.id}/settings/${s.id}`,
          label: s.label,
          end: true,
        })),
      },
    ] : []),
    // v2.5.118 — Leagues nav is gated by the per-user 'leagues'
    // feature flag. Off by default; admins flip it on per-user
    // from /admin. The backend also gates every /api/leagues*
    // endpoint with requireFeatureFlag('leagues'), so even if a
    // user manually navigates to /leagues, the page loads but
    // every API call returns 404.
  ] : [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
  ];

  // Append Leagues if the flag is set. Done outside the
  // activeTeam-ternary so the entry shape is identical whether
  // the user has a team or not.
  if (user?.featureFlags?.leagues) {
    navItems.push({ to: '/leagues', icon: Trophy, label: 'Leagues', accent: 'gold' });
  }
  // 2026-10-17 — Press Box (single-team stats), gated like Leagues: the
  // pressBox flag, or the super admin. The server re-checks every call.
  if (pressBoxVisible(user) && activeTeam?.team_type !== 'press_box') {
    navItems.push({ to: '/press-box', icon: Radio, label: PRESS_BOX.NAME, accent: 'gold' });
  }

  // v2.5.124 — see DesktopLayout for rationale.
  const { inLeagueMode, leagueNavItems, league } = useLeagueModeNav();

  // 2026-10-22 (per Gilles) — an account whose teams are ALL Press Box sees
  // ONLY the Press Box menu, on every surface (web, desktop, mobile app).
  const pbOnlyTeams = (teams || []).filter(t => t.team_type === 'press_box');
  const pressBoxOnlyAccount = pbOnlyTeams.length > 0 && pbOnlyTeams.length === (teams || []).length;
  const navItemsPb = pressBoxOnlyAccount && activeTeam?.team_type !== 'press_box'
    ? pressBoxNavItems(pbOnlyTeams[0], { Radio, Users, Settings, BarChart3: BarChart2, Globe, Trophy })
    : navItems;
  const effectiveNavItems = inLeagueMode && leagueNavItems ? leagueNavItems : navItemsPb;

  // (Apr 2026 — the previous `bottomNavItems` constant for an in-drawer
  // bottom row was removed. Alerts and Profile now live inline at the
  // bottom of the drawer body, and the persistent BottomNav handles
  // primary navigation outside the drawer.)

  return (
    <>
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{
          position: 'fixed', inset: 0, zIndex: 490,
          background: 'rgba(0,0,0,0.65)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.22s ease',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        style={{
          position: 'fixed', top: 0, bottom: 0, left: 0,
          width: 288, zIndex: 500,
          background: `linear-gradient(160deg, ${S.surface} 0%, ${S.bg} 100%)`,
          borderRight: `1px solid ${S.border}`,
          transform: open ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.28s cubic-bezier(0.32,0.72,0,1)',
          willChange: 'transform',
          display: 'flex', flexDirection: 'column',
          overflowY: 'auto', overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'calc(env(safe-area-inset-top, 0px) + 18px) 16px 14px',
          borderBottom: `1px solid ${S.border}`, flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `${teamColor}18`, border: `1px solid ${teamColor}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
            }}>🏟</div>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 20, letterSpacing: '0.08em', textTransform: 'uppercase', color: S.text, lineHeight: 1 }}>Game Day</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 8.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: teamColor, marginTop: 3 }}>TEAM MANAGER</div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close menu" style={{ ...T, padding: 10, borderRadius: 10, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={18} color={S.text2} />
          </button>
        </div>

        {/* League-mode drawer header — v2.5.124. */}
        {inLeagueMode && (
          <div style={{ padding: '12px 12px 6px', flexShrink: 0 }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: S.text2, padding: '0 6px', marginBottom: 8 }}>LEAGUE MODE</p>
            <div style={{
              padding: '11px 12px', borderRadius: 10, marginBottom: 4,
              background: 'rgba(178, 132, 255, 0.10)',
              border: '1px solid rgba(178, 132, 255, 0.30)',
              display: 'flex', alignItems: 'center', gap: 10, minWidth: 0,
            }}>
              <Trophy size={14} style={{ color: '#b284ff', flexShrink: 0 }} />
              <span style={{
                fontSize: 14, fontWeight: 700, color: S.text,
                overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                flex: 1, minWidth: 0,
              }}>{league?.name || 'Loading…'}</span>
            </div>
            <button
              onClick={() => { navigate('/dashboard'); onClose(); }}
              style={{
                ...T, width: '100%', textAlign: 'left',
                padding: '11px 12px', borderRadius: 10, marginTop: 4, minHeight: 44,
                background: 'transparent', border: `1px dashed ${S.border}`,
                display: 'flex', alignItems: 'center', gap: 10,
                color: S.text2, fontFamily: 'var(--font-body)', fontSize: 14,
              }}>
              <ChevronLeft size={14} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600 }}>Back to teams</span>
            </button>
          </div>
        )}

        {!inLeagueMode && teams.length > 0 && (
          <div style={{ padding: '12px 12px 6px', flexShrink: 0 }}>
            <p style={{ fontFamily: 'var(--font-display)', fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: S.text2, padding: '0 6px', marginBottom: 8 }}>YOUR TEAMS</p>
            {teams.map(t => {
              const c = t.settings?.team_color || 'var(--ice)';
              const isA = activeTeam?.id === t.id;
              return (
                <button key={t.id} onClick={() => {
                  // Apr 30 2026 — see DesktopLayout for rationale.
                  setActiveTeam(t);
                  if (activeTeam?.id !== t.id) {
                    const target = pathForTeamSwitch(location.pathname, activeTeam?.id, t.id, t);
                    if (target) navigate(target);
                  }
                  onClose();
                }} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px', borderRadius: 10, marginBottom: 3, minHeight: 48, background: isA ? `${c}12` : 'transparent', border: `1px solid ${isA ? `${c}28` : 'transparent'}` }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: c, opacity: isA ? 1 : 0.3, flexShrink: 0, boxShadow: isA ? `0 0 8px ${c}` : 'none' }} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: isA ? 600 : 400, color: isA ? c : S.text2, fontFamily: 'var(--font-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                  {isA && <ChevronRight size={14} color={c} strokeWidth={2.5} />}
                </button>
              );
            })}
            {/* Apr 2026 — "+ New Team" so any player can create their
                own team from the mobile drawer without having to find
                the button on the dashboard header. Dashed border
                separates it visually from the actual teams list. */}
            <button
              onClick={() => { navigate('/dashboard?new=1'); onClose(); }}
              style={{
                ...T, width: '100%', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 12px', borderRadius: 10, marginTop: 4, minHeight: 48,
                background: 'transparent',
                border: `1px dashed ${S.border}`,
                color: S.text2, fontFamily: 'var(--font-body)', fontSize: 14,
              }}>
              <Plus size={16} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, fontWeight: 600 }}>New Team</span>
            </button>
          </div>
        )}

        <div style={{ height: 1, background: S.border, margin: '4px 16px 8px', flexShrink: 0 }} />

        <nav style={{ flex: 1, padding: '4px 12px' }}>
          {activeTeam && (
            <button onClick={() => { navigate(`/team/${activeTeam.id}/seasons`); onClose?.(); }}
              title="The season every page is currently showing. Tap to switch."
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', margin: '0 0 8px', padding: '9px 12px', borderRadius: 10,
                background: currentSeasonId ? 'var(--ice-a8, rgba(0,212,255,0.08))' : 'var(--surface-2)',
                border: `1px solid ${currentSeasonId ? 'var(--ice-a30, rgba(0,212,255,0.30))' : S.border}`, cursor: 'pointer', textAlign: 'left' }}>
              <Layers size={15} style={{ color: currentSeasonId ? 'var(--ice)' : 'var(--text-3)', flexShrink: 0 }} />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', lineHeight: 1.2 }}>Season</span>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: currentSeasonId ? 'var(--ice)' : 'var(--text-2)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {currentSeasonId ? (currentSeasonName || 'Selected season') : 'All seasons'}
                </span>
              </span>
            </button>
          )}
          {effectiveNavItems.map(item => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const expanded    = hasChildren && location.pathname.startsWith(item.to);
            // v2.5.126 — gold accent for Leagues nav item.
            const itemAccentColor = item.accent === 'gold' ? 'var(--gold)' : teamColor;
            const itemAccentBg    = item.accent === 'gold' ? 'var(--gold-a15)' : `${teamColor}12`;
            const itemAccentBd    = item.accent === 'gold' ? 'var(--gold-a30)' : `${teamColor}28`;
            const itemIdleColor   = item.accent === 'gold' ? 'var(--gold-a50)' : S.text2;
            return (
              <div key={item.to}>
                <NavLink to={item.to} end={item.end} style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 3,
                  textDecoration: 'none', minHeight: 48,
                  background: isActive ? itemAccentBg : 'transparent',
                  border: `1px solid ${isActive ? itemAccentBd : 'transparent'}`,
                  color: isActive ? itemAccentColor : itemIdleColor,
                  fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: isActive ? 600 : 400,
                  contain: 'layout style',
                })}>
                  <item.icon size={18} strokeWidth={1.8} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {hasChildren && (
                    <ChevronDown size={16} style={{
                      flexShrink: 0,
                      transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.18s',
                      opacity: 0.6,
                    }} />
                  )}
                </NavLink>
                {expanded && (
                  <div style={{ marginLeft: 12, marginBottom: 6, borderLeft: `1.5px solid ${S.border}`, paddingLeft: 4 }}>
                    {item.children.map(child => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        style={({ isActive: childActive }) => ({
                          display: 'block',
                          padding: '10px 14px',
                          marginBottom: 2,
                          borderRadius: 8,
                          textDecoration: 'none',
                          fontFamily: 'var(--font-body)',
                          fontSize: 14,
                          fontWeight: childActive ? 600 : 400,
                          color: childActive ? teamColor : S.text2,
                          background: childActive ? `${teamColor}10` : 'transparent',
                          minHeight: 40,
                        })}>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div style={{ padding: '8px 12px', borderTop: `1px solid ${S.border}`, flexShrink: 0 }}>
          <button onClick={() => { onNotifsClick(); onClose(); }} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 3, minHeight: 48, color: S.text2, fontFamily: 'var(--font-body)', fontSize: 15, position: 'relative' }}>
            <Bell size={18} strokeWidth={1.8} />
            Alerts
            {(unreadNotifs || 0) > 0 && (
              <span style={{ minWidth: 18, height: 18, padding: '0 4px', borderRadius: 9, background: 'var(--penalty)', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {unreadNotifs > 9 ? '9+' : unreadNotifs}
              </span>
            )}
          </button>
          <NavLink to="/profile" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 3, textDecoration: 'none', minHeight: 48, background: isActive ? 'var(--ice-a8)' : 'transparent', color: isActive ? 'var(--ice)' : S.text2, fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500 })}>
            <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: 'var(--ice-a12)', border: '1px solid var(--ice-a25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, color: 'var(--ice)' }}>
              {user?.name?.[0]?.toUpperCase() || '?'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: '#00e87a', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>● ONLINE</div>
            </div>
          </NavLink>
          {/* Apr 2026 — Activity link. Only rendered when the user has
              at least one payment or credit row (flag comes from
              /auth/me). Players who've never paid a spare/season fee
              don't see this link; adding it only when it's relevant
              keeps the drawer clean for the common case. */}
          {user?.hasPaymentActivity && (
            <NavLink to="/activity" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 3, textDecoration: 'none', minHeight: 48, background: isActive ? 'var(--ice-a8)' : 'transparent', color: isActive ? 'var(--ice)' : S.text2, fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 500 })}>
              <Receipt size={16} /> Activity
            </NavLink>
          )}
          {user?.role === 'admin' && (
            <NavLink to="/admin" style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, marginBottom: 3, textDecoration: 'none', minHeight: 48, color: isActive ? '#ff6040' : 'rgba(255,96,64,0.5)', fontFamily: 'var(--font-body)', fontSize: 15 })}>
              <Shield size={16} /> Admin
            </NavLink>
          )}
          <button onClick={onLogout} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, minHeight: 48, color: 'rgba(255,80,80,0.65)', fontFamily: 'var(--font-body)', fontSize: 15 }}>
            <LogOut size={16} /> Sign out
          </button>
          {/* Apr 30 2026 — version tag at the very bottom of the drawer.
              Tappable for build details (frontend + backend versions
              + drift warning if they don't match). Tiny by default
              so it's noise unless the user is looking for it. */}
          <div style={{ paddingTop: 4, borderTop: `1px solid ${S.border}`, marginTop: 4 }}>
            <VersionTag />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Notification sheet ───────────────────────────────────────────
function NotifSheet({ notifications, onClose, onMarkRead, onMarkAllRead }) {
  // Apr 2026 — icons now come from utils/notifIcons.js (shared across layouts).
  return (
    <>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 490, background: 'rgba(0,0,0,0.55)' }} />
      <div role="dialog" aria-modal="true" aria-label="Notifications" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 500, background: S.surface, borderBottom: `1px solid ${S.border}`, maxHeight: '72dvh', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', contain: 'layout style paint', transform: 'translateZ(0)', paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: `1px solid ${S.border}`, position: 'sticky', top: 'env(safe-area-inset-top, 0px)', background: S.surface, zIndex: 1 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 15, letterSpacing: '0.06em', textTransform: 'uppercase', color: S.text }}>Notifications</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onMarkAllRead} style={{ ...T, fontSize: 12, color: 'var(--ice)', padding: '7px 12px', borderRadius: 7, background: 'var(--ice-a10)', border: '1px solid var(--ice-a20)', fontFamily: 'var(--font-body)', fontWeight: 600, minHeight: 44 }}>All read</button>
            <button onClick={onClose} aria-label="Close" style={{ ...T, padding: 8, borderRadius: 8, minWidth: 44, minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} color={S.text2} /></button>
          </div>
        </div>
        {notifications.length === 0 ? (
          <div style={{ padding: '44px 20px', textAlign: 'center', color: S.text2, fontSize: 14 }}>
            <Bell size={30} style={{ margin: '0 auto 14px', opacity: 0.18, display: 'block' }} />
            All caught up!
          </div>
        ) : notifications.slice(0, 20).map(n => (
          <button key={n.id} onClick={() => onMarkRead(n.id)} style={{ ...T, width: '100%', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 16px', borderBottom: `1px solid ${S.border}`, background: n.read_at ? 'transparent' : 'var(--ice-a3)', minHeight: 60 }}>
            <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{iconForNotifType(n.type)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: S.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                {!n.read_at && <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ice)', flexShrink: 0 }} />}
              </div>
              {n.body && <p style={{ fontSize: 13, color: S.text2, margin: '3px 0 0', lineHeight: 1.42 }}>{n.body}</p>}
              <p style={{ fontSize: 11, color: S.text2, opacity: 0.45, marginTop: 4 }}>
                {new Date(n.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// ─── Bottom tab bar ───────────────────────────────────────────────
// hasBrowserChrome=true (Chrome/Firefox Android): the OS navigation bar sits OUTSIDE
// the visualViewport — so our tab bar already lands within the visible area.
// We just need env(safe-area-inset-bottom) which is 0 on Android anyway.
// hasBrowserChrome=false (Safari iOS): safe-area-inset-bottom is 34px on iPhone X+,
// covering the home indicator. Both cases handled identically — env() does the right thing.
function MobileTabBar({ activeTeam, unreadCount, unreadNotifs, onNotifClick, teamColor }) {
  const location = useLocation();
  const color    = teamColor || 'var(--ice)';

  const tabs = [
    { to: '/dashboard',                     icon: LayoutDashboard, label: 'Home',     end: true },
    activeTeam && { to: `/team/${activeTeam.id}/games`, icon: Calendar,      label: 'Schedule' },
    activeTeam && { to: `/team/${activeTeam.id}/chat`,  icon: MessageSquare, label: 'Chat', badge: unreadCount },
    { onClick: onNotifClick,                icon: Bell,            label: 'Alerts',  badge: unreadNotifs },
    { to: '/profile',                       icon: User,            label: 'Profile' },
  ].filter(Boolean);

  return (
    <nav
      aria-label="Main navigation"
      data-tab-bar
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        zIndex: 400,
        background: S.bottomBar,
        borderTop: `1px solid ${S.border}`,
        // iOS home indicator handled by safe-area-inset-bottom.
        // Android: env() = 0 so no extra padding wasted.
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        display: 'flex', alignItems: 'stretch',
        transform: 'translateZ(0)',
        contain: 'layout style paint',
      }}>
      {tabs.map((tab, i) => {
        const isActive = tab.to && (tab.end ? location.pathname === tab.to : location.pathname.startsWith(tab.to));
        const tabColor = isActive ? color : 'var(--tab-inactive)';
        const inner = (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '10px 0 8px', position: 'relative', width: '100%' }}>
            {isActive && <span style={{ position: 'absolute', top: 5, width: 22, height: 2, borderRadius: 1, background: color, boxShadow: `0 0 7px ${color}` }} />}
            <span style={{ position: 'relative' }}>
              <tab.icon size={22} color={tabColor} strokeWidth={isActive ? 2.2 : 1.7} />
              {(tab.badge || 0) > 0 && (
                <span style={{ position: 'absolute', top: -4, right: -6, minWidth: 16, height: 16, padding: '0 3px', borderRadius: 8, background: '#ff3b3b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                  {tab.badge > 9 ? '9+' : tab.badge}
                </span>
              )}
            </span>
            <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, fontFamily: 'var(--font-display)', letterSpacing: isActive ? '0.07em' : '0.03em', textTransform: 'uppercase', color: tabColor }}>{tab.label}</span>
          </span>
        );
        const shared = {
          key: i,
          style: { ...T, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' },
          // Opacity only — zero repaints during touch
          onTouchStart(e) { e.currentTarget.style.opacity = '0.65'; },
          onTouchEnd(e)   { e.currentTarget.style.opacity = '1'; },
          onTouchCancel(e){ e.currentTarget.style.opacity = '1'; },
        };
        return tab.onClick
          ? <button {...shared} onClick={tab.onClick}>{inner}</button>
          : <NavLink {...shared} to={tab.to} end={tab.end}>{inner}</NavLink>;
      })}
    </nav>
  );
}

// ─── MobileLayout ─────────────────────────────────────────────────
export default function MobileLayout({ browserType, hasBrowserChrome } = {}) {
  const { user, logout } = useAuth();
  const { offline }      = usePWAContext();
  const navigate         = useNavigate();
  const location         = useLocation();

  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [showNotifs,    setShowNotifs]    = useState(false);
  const [showTeamPicker, setShowTeamPicker] = useState(false);
  const [teams,         setTeams]         = useState([]);
  // May 1 2026 — see DesktopLayout for rationale.
  const [teamsLoaded,   setTeamsLoaded]   = useState(false);
  const [activeTeam,    setActiveTeam]    = useState(null);
  const { currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded } = useCurrentSeason(activeTeam?.id || null);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [notifications, setNotifications] = useState([]);

  const teamColor = activeTeam?.settings?.team_color || null;
  // For CSS property values (color, border, etc.) — safe to use var(--ice) as a value
  const teamAccent = teamColor || 'var(--ice)';

  useEffect(() => {
    if (teamColor) {
      // Custom team color — override --ice globally
      document.documentElement.style.setProperty('--ice', teamColor);
    } else {
      // No custom color — remove inline override so the theme default applies
      document.documentElement.style.removeProperty('--ice');
    }
    return () => document.documentElement.style.removeProperty('--ice');
  }, [teamColor]);

  const loadTeams = useCallback(async (lastTeamId = null, _attempt = 0) => {
    try {
      const { data } = await api.get('/teams');
      setTeams(data);
      // Apr 2026 — pickActiveTeam uses the saved last-team id we
      // pass in (from the user object) to restore the user's
      // selection. See utils/lastTeam.js. Passing as a parameter
      // (rather than reading user via closure) avoids the stale-
      // closure bug — the useCallback has empty deps so a closure
      // read would capture user=null at mount time.
      setActiveTeam(prev => pickActiveTeam(data, prev, lastTeamId));
      setTeamsLoaded(true);
    } catch (err) {
      // v2.5.184 — see DesktopLayout for full rationale. Don't let a
      // transient 429/5xx/network failure render the misleading
      // "not on a schedule" empty state. Retry once, then give up.
      console.error('Failed to load teams:', err);
      const status = err?.response?.status;
      if (status === 401) { setTeamsLoaded(true); return; }
      if (_attempt < 1) { setTimeout(() => loadTeams(lastTeamId, _attempt + 1), 1500); return; }
      setTeamsLoaded(true);
    }
  }, []);

  // Apr 2026 — persist the active team id to users.last_active_team_id
  // (server side, not localStorage) so the choice syncs across
  // devices. Fire-and-forget.
  useEffect(() => {
    if (activeTeam?.id) saveLastTeamId(activeTeam.id);
  }, [activeTeam?.id]);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch {}
  }, []);

  useEffect(() => {
    // Apr 2026 — gate on user.id so we know the user object has
    // loaded before reading lastActiveTeamId. Without the gate,
    // loadTeams runs before /auth/me resolves and falls back to
    // teams[0], silently ignoring the saved team preference.
    if (user?.id) {
      loadTeams(user.lastActiveTeamId || null);
    }
    const token = localStorage.getItem('token');
    if (token) chatSocket.connect(token);
    return () => chatSocket.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // 2026-07-21 (multi-team email-link landing audit) — correct activeTeam
  // when it drifts from the team the CURRENT URL is actually pointing at.
  // See DesktopLayout.jsx for the full rationale — same fix, same shape,
  // applied to every layout since each keeps its own activeTeam state.
  useEffect(() => {
    if (!teamsLoaded || teams.length === 0) return;
    const match = location.pathname.match(/^\/team\/([^/]+)/);
    const urlTeamId = match ? match[1] : null;
    if (!urlTeamId || urlTeamId === activeTeam?.id) return;
    const urlTeam = teams.find(t => t.id === urlTeamId);
    if (!urlTeam) return; // not a member (or a bad id) — leave activeTeam as-is
    setActiveTeam(urlTeam);
    try { localStorage.setItem('gd:lastTeamId', urlTeamId); } catch { /* storage unavailable — non-fatal */ }
  }, [location.pathname, teams, teamsLoaded, activeTeam?.id]);

  useEffect(() => { loadNotifications(); }, []);

  useEffect(() => {
    const id = setInterval(loadNotifications, 45_000);
    return () => clearInterval(id);
  }, [loadNotifications]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_MESSAGE', () => {
      if (!location.pathname.includes('/chat')) setUnreadCount(c => c + 1);
    });
    return unsub;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.includes('/chat')) setUnreadCount(0);
  }, [location.pathname]);

  useEffect(() => {
    const unsub = chatSocket.on('NEW_NOTIFICATION', notif => {
      if (notif) setNotifications(prev => [{ ...notif, read_at: null }, ...prev.filter(n => n.id !== notif.id)]);
      else loadNotifications();
    });
    return unsub;
  }, [loadNotifications]);

  // ── Viewport height tracking ─────────────────────────────────────
  //
  // Apr 2026 rewrite (take 6, decisive): drive --mobile-h from JS on
  // every resize/scroll event. CSS viewport units (vh/svh/dvh/lvh) on
  // iOS Safari are famously unreliable — the docs promise dvh tracks
  // the dynamic viewport, but Safari 17+ has documented bugs where
  // the value lags behind the actual visible area during URL-bar
  // collapse/expand transitions. The resulting "dead band" between
  // the app's bottom and Safari's toolbar is what users report as
  // "empty space that's not being used".
  //
  // Strategy: visualViewport.height is the one number iOS Safari
  // always reports correctly. Write it into --mobile-h every time
  // it changes. Root uses var(--mobile-h, 100dvh) so the first
  // paint (before this effect runs) uses the CSS fallback, but from
  // the first resize tick onwards we have a pixel-exact height that
  // matches what the user actually sees.
  //
  // KEYBOARD DETECTION still matters — when the keyboard is up, we
  // set an additional --keyboard-h var so components can react. See
  // the isEditableFocused check below.
  //
  // DEBUG OVERLAY: append ?vpdebug=1 to any URL to show a live
  // overlay with the four viewport numbers. Helps diagnose
  // reported issues remotely without requiring Safari Web
  // Inspector.
  useEffect(() => {
    const vv = window.visualViewport;
    const isEditableFocused = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
    };
    const debugOn = (() => {
      try { return new URLSearchParams(location.search).has('vpdebug'); } catch { return false; }
    })();
    let debugEl = null;
    if (debugOn) {
      debugEl = document.createElement('div');
      debugEl.style.cssText = 'position:fixed;top:env(safe-area-inset-top,0);left:0;right:0;background:rgba(0,0,0,0.85);color:#0ff;font:11px/1.3 monospace;padding:4px 8px;z-index:99999;pointer-events:none;white-space:pre;';
      document.body.appendChild(debugEl);
    }
    const update = () => {
      const windowH = window.innerHeight;
      const vvH = vv ? vv.height : windowH;
      const vvOffsetTop = vv ? vv.offsetTop : 0;
      const delta = windowH - vvH;
      const keyboardVisible = isEditableFocused() && delta > 150;

      // ── 2026-08-08 (mobile cut-off report) — DON'T pin the shell to a
      // JS-measured pixel height unless the keyboard is actually up.
      //
      // The previous version wrote `--mobile-h = visualViewport.height`
      // on EVERY resize AND scroll tick. On iOS Safari vv.height reports
      // transient mid-animation values while the URL bar collapses or
      // expands, and vv.scroll fires continuously during a scroll. The
      // shell (height: var(--mobile-h)) latched onto one of those
      // too-small values and stayed there: the app ended well above
      // Safari's toolbar (a dead band of empty page) and the fixed
      // bottom nav came to rest ON TOP of the last row of content — on
      // the game page, the I'm In / Waitlist / I'm Out buttons, visible
      // through the translucent bar and untappable. Exactly the report.
      //
      // Correct behaviour: let CSS own the no-keyboard case. 100dvh IS
      // the dynamic viewport and Safari resolves it at paint time with
      // no thrash — the Capacitor path already reached this conclusion
      // (useCapacitorPlugins sets '100dvh' on keyboard hide). Override
      // with a measured pixel height ONLY when the keyboard is up,
      // where dvh does not shrink and measuring is the only option.
      if (keyboardVisible) {
        document.documentElement.style.setProperty('--mobile-h', `${vvH}px`);
      } else {
        document.documentElement.style.setProperty('--mobile-h', '100dvh');
      }
      document.documentElement.style.setProperty(
        '--keyboard-h',
        keyboardVisible ? `${delta}px` : '0px'
      );
      // 2026-08-08 — mirror Capacitor's class on mobile web so
      // index.css can lift `[data-tab-bar]` above the keyboard here too.
      // Without it the tab bar sat under the keyboard whenever a player
      // typed in a phone browser (chat, notes, any form).
      document.documentElement.classList.toggle('keyboard-open', keyboardVisible);

      if (debugEl) {
        debugEl.textContent =
          `inner: ${windowH}  visual: ${Math.round(vvH)}  ` +
          `vv.top: ${Math.round(vvOffsetTop)}  Δ: ${delta}\n` +
          `--mobile-h: ${keyboardVisible ? `${vvH}px` : '100dvh'}  ` +
          `kb: ${keyboardVisible ? 'YES' : 'no'}  ` +
          `nav-h: ${getComputedStyle(document.documentElement).getPropertyValue('--bottom-nav-h') || '(unset)'}  ` +
          `dpr: ${window.devicePixelRatio}`;
      }
    };
    update();
    if (vv) {
      vv.addEventListener('resize', update, { passive: true });
      // 2026-08-08 — vv.scroll listener REMOVED. Scrolling changes the
      // visual viewport's OFFSET, not its size, so this listener never
      // contributed a needed height update — it only fired `update()`
      // dozens of times per scroll, which is how a transient mid-
      // animation height got latched into --mobile-h. Keyboard
      // show/hide arrives via resize + focusin/focusout, which we
      // still listen to.
    }
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });
    document.addEventListener('visibilitychange', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      if (vv) vv.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      document.removeEventListener('visibilitychange', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      // Leaving the layout (e.g. rotating into the tablet breakpoint)
      // must not strand a keyboard-open class on <html>.
      document.documentElement.classList.remove('keyboard-open');
      if (debugEl) debugEl.remove();
    };
  }, []);

  const handleLogout = useCallback(() => { logout(); navigate('/login'); }, [logout, navigate]);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const unreadNotifs = notifications.filter(n => !n.read_at).length;

  const markRead = async id => {
    // Capture nav target BEFORE the await — notifications state captured in this
    // closure may be stale if a WS event arrives during the API call
    const notif = notifications.find(n => n.id === id);
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(p => p.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      if (notif?.game_id) navigate(`/game/${notif.game_id}`);
      else if (notif?.team_id) navigate(`/team/${notif.team_id}/chat`);
      setShowNotifs(false);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(p => p.map(n => ({ ...n, read_at: new Date().toISOString() })));
    } catch {}
    setShowNotifs(false);
  };

  const TABBAR_H = 60;

  // Apr 30 2026 (App_Change_List #2) — cycle prev/next through the
  // user's teams. Wraps around at the ends so a coach with two
  // teams can flip back and forth with one button. Single-team
  // users see no cycle UI at all (the parent JSX guards on
  // teams.length > 1 before rendering the buttons).
  //
  // URL rewrite: same logic as the dropdown picker — we use
  // pathForTeamSwitch so a coach on /team/:id/players moves to
  // /team/<newId>/players, not back to dashboard. Pages that
  // can't translate (e.g. a per-game URL with the old team's
  // game id) fall back to the new team's dashboard inside the
  // helper.
  const cycleToTeam = useCallback((direction) => {
    if (!Array.isArray(teams) || teams.length <= 1) return;
    const idx = teams.findIndex(t => t.id === activeTeam?.id);
    if (idx === -1) return;
    const nextIdx = direction === 'next'
      ? (idx + 1) % teams.length
      : (idx - 1 + teams.length) % teams.length;
    const nextTeam = teams[nextIdx];
    if (!nextTeam || nextTeam.id === activeTeam?.id) return;
    setActiveTeam(nextTeam);
    // The page-level useEffect on activeTeam.id handles saveLastTeamId
    // already — no explicit call needed here, mirroring the dropdown
    // picker's behaviour.
    const target = pathForTeamSwitch(location.pathname, activeTeam?.id, nextTeam.id, nextTeam);
    if (target) navigate(target);
  }, [teams, activeTeam, location.pathname, navigate]);

  return (
    <div style={{
      // Apr 2026: using 100dvh. This tracks the current visible
      // viewport on iOS Safari (shrinks when URL bar shows, grows
      // when it hides). `min-height: 100%` on inner content prevents
      // dead zones below short pages. Bottom padding on the scroll
      // container clears Safari's home indicator via env(safe-area-
      // inset-bottom) + 20px buffer.
      //
      // --mobile-h is set by the Visual Viewport observer when the
      // keyboard is up, overriding dvh in that case to shrink to
      // the keyboard-reduced visual viewport.
      height: 'var(--mobile-h, 100dvh)',
      display: 'flex', flexDirection: 'column',
      background: S.bg,
      overflow: 'hidden',
      overscrollBehavior: 'none',
    }}>

      {/* Offline strip */}
      {offline.isOffline && (
        <div style={{
          background: 'rgba(255,59,59,0.95)',
          padding: '7px 16px',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 7px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          fontFamily: 'var(--font-display)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff',
          flexShrink: 0,
        }}>
          <WifiOff size={13} />
          <span>Offline — cached data</span>
        </div>
      )}

      {/* Top bar — translateZ(0) promotes to own GPU layer */}
      <div style={{
        background: S.topBar,
        borderBottom: `1px solid ${S.border}`,
        paddingTop: offline.isOffline ? 0 : 'env(safe-area-inset-top, 0px)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        height: `calc(${offline.isOffline ? '0px' : 'env(safe-area-inset-top, 0px)'} + 52px)`,
        padding: `${offline.isOffline ? '0' : 'env(safe-area-inset-top, 0px)'} 12px 0`,
        paddingBottom: 0,
        flexShrink: 0,
        contain: 'layout style paint',
        transform: 'translateZ(0)',
        zIndex: 300,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: 52, paddingLeft: 2, flex: 1, minWidth: 0 }}>
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            style={{ ...T, padding: 8, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44, flexShrink: 0 }}
          >
            <Menu size={22} color={S.text2} strokeWidth={1.8} />
          </button>
          <span style={{ fontSize: 20, lineHeight: 1, pointerEvents: 'none', flexShrink: 0 }}>🏟</span>
          {/* Apr 30 2026 (App_Change_List #2) — "Game Day" wordmark
              dropped on mobile. The team name IS the brand identity
              when you're inside a team's context, and the wordmark
              was eating horizontal space that the team name needed.
              The 🏟 emoji + team name still convey "this is Game
              Day, and you're on team X" clearly. The wordmark
              still appears in the drawer header and on desktop. */}
          {activeTeam && teams.length > 0 && (
            <div style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', gap: 2,
              flex: 1, minWidth: 0,
            }}>
              {/* Apr 30 2026 (App_Change_List #2) — prev-team cycle
                  button. Hidden when only one team. Wraps to the last
                  team at the start of the list so coaches can flip
                  forwards or backwards with single taps. */}
              {teams.length > 1 && (
                <button
                  onClick={() => cycleToTeam('prev')}
                  aria-label="Previous team"
                  style={{
                    ...T,
                    padding: 6, borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 32, minHeight: 32, flexShrink: 0,
                  }}>
                  <ChevronLeft size={18} color={teamAccent} strokeWidth={2} />
                </button>
              )}
              <button
                onClick={() => { if (teams.length > 1) setShowTeamPicker(v => !v); }}
                title={teams.length > 1 ? `Tap to switch team (${teams.length} teams)` : activeTeam.name}
                style={{
                  background: 'none', border: 'none', cursor: teams.length > 1 ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', gap: 3,
                  fontSize: 13, color: teamAccent, fontFamily: 'var(--font-body)', fontWeight: 700,
                  padding: '4px 6px', borderRadius: 5,
                  flex: 1, minWidth: 0,
                }}>
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1, minWidth: 0, textAlign: 'left',
                }}>
                  {activeTeam.name}
                </span>
                {teams.length > 1 && (
                  <span style={{ fontSize: 9, opacity: 0.7, flexShrink: 0 }}>▾</span>
                )}
              </button>
              {teams.length > 1 && (
                <button
                  onClick={() => cycleToTeam('next')}
                  aria-label="Next team"
                  style={{
                    ...T,
                    padding: 6, borderRadius: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 32, minHeight: 32, flexShrink: 0,
                  }}>
                  <ChevronRight size={18} color={teamAccent} strokeWidth={2} />
                </button>
              )}
              {showTeamPicker && teams.length > 1 && (
                <>
                  {/* Click-off backdrop — closes picker when the user taps outside it */}
                  <div
                    onClick={() => setShowTeamPicker(false)}
                    style={{
                      position: 'fixed', inset: 0, zIndex: 299,
                      background: 'transparent',
                    }}
                  />
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 400,
                    background: S.surface, border: `1px solid ${S.border}`,
                    borderRadius: 12, padding: 6,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    minWidth: 220, maxWidth: 280,
                    maxHeight: 360, overflowY: 'auto',
                  }}>
                    <p style={{
                      fontFamily: 'var(--font-display)', fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.15em', textTransform: 'uppercase',
                      color: S.text2, padding: '6px 10px 4px', margin: 0,
                    }}>
                      Switch Team ({teams.length})
                    </p>
                    {teams.map(t => {
                      const isA = activeTeam.id === t.id;
                      const c   = t.settings?.team_color || 'var(--ice)';
                      const role = (t.member_role || '').toLowerCase();
                      const isCoachBadge = ['coach', 'manager'].includes(role);
                      return (
                        <button
                          key={t.id}
                          onClick={() => {
                            // Apr 30 2026 — also rewrite URL on team
                            // switch (see pathForTeamSwitch).
                            setActiveTeam(t);
                            if (activeTeam?.id !== t.id) {
                              const target = pathForTeamSwitch(location.pathname, activeTeam?.id, t.id, t);
                              if (target) navigate(target);
                            }
                            setShowTeamPicker(false);
                          }}
                          style={{
                            width: '100%', textAlign: 'left',
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '9px 10px', borderRadius: 8,
                            background: isA ? `${c}14` : 'transparent',
                            border: `1px solid ${isA ? `${c}30` : 'transparent'}`,
                            cursor: 'pointer', marginBottom: 2,
                            minHeight: 40,
                          }}>
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                            background: c, opacity: isA ? 1 : 0.4,
                            boxShadow: isA ? `0 0 6px ${c}` : 'none',
                          }} />
                          <span style={{
                            flex: 1, fontSize: 13,
                            fontWeight: isA ? 700 : 500,
                            color: isA ? c : S.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {t.name}
                          </span>
                          {isCoachBadge && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, letterSpacing: '0.04em',
                              padding: '2px 5px', borderRadius: 4,
                              background: isA ? `${c}22` : 'var(--surface-3)',
                              color: isA ? c : S.text2, flexShrink: 0,
                              textTransform: 'uppercase',
                            }}>
                              {role}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ height: 52, display: 'flex', alignItems: 'center', paddingRight: 2 }}>
          <button
            onClick={() => { setShowNotifs(p => !p); loadNotifications(); }}
            aria-label={`Notifications${unreadNotifs > 0 ? ` (${unreadNotifs} unread)` : ''}`}
            style={{ ...T, padding: 8, borderRadius: 8, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44 }}
          >
            <Bell size={20} color={unreadNotifs > 0 ? teamAccent : S.text2} strokeWidth={1.8} />
            {unreadNotifs > 0 && (
              <span style={{ position: 'absolute', top: 7, right: 7, minWidth: 16, height: 16, padding: '0 3px', borderRadius: 8, background: '#ff3b3b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', lineHeight: 1 }}>
                {unreadNotifs > 9 ? '9+' : unreadNotifs}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* App store smart banner (mobile browsers only — hidden in Capacitor and standalone PWA) */}
      <AppBanner />

      {/* Scrollable content */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        // 2026-08-08 — clearance is now MEASURED, not guessed. The old
        // value hardcoded "the bar is roughly 54px" + 12px; BottomNav
        // now publishes its real rendered height (including its own
        // safe-area padding) as --bottom-nav-h, so this can never drift
        // from the bar again. The 58px fallback covers the first paint
        // before the measurement lands, and --keyboard-h keeps the last
        // field clear of the on-screen keyboard (matching the
        // CapacitorLayout formula).
        //
        // NOTE: --bottom-nav-h already INCLUDES env(safe-area-inset-
        // bottom) because the bar pads itself with it — do not add the
        // inset again here or every phone with a home indicator gets
        // 34px of dead space at the end of every page.
        paddingBottom: 'calc(var(--bottom-nav-h, 58px) + var(--keyboard-h, 0px) + 16px)',
      }}>
        <div style={{
          padding: '14px 14px 0',
          maxWidth: 600,
          margin: '0 auto',
          // min-height:100% prevents short pages from showing the
          // scroll container's background below the last card.
          minHeight: '100%',
          display: 'block',
        }}>
          <Outlet context={{ teams, teamsLoaded, activeTeam, setActiveTeam, loadTeams, currentSeasonId, currentSeasonName, setCurrentSeason, seasonLoaded }} />
        </div>
      </div>

      {/* Onboarding overlay — shown once on first login */}
      <OnboardingFlow teams={teams} activeTeam={activeTeam} />

      {/* PWA banners — update-available / push-nudge / offline.
          Mounted at the layout level so every protected route on
          mobile sees app-update notices, not just desktop. ── */}
      <PWABanners />

      {/* Drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        teams={teams}
        activeTeam={activeTeam}
        setActiveTeam={setActiveTeam}
        user={user}
        onLogout={handleLogout}
        onNotifsClick={() => { setShowNotifs(p => !p); loadNotifications(); }}
        unreadNotifs={unreadNotifs}
        currentSeasonId={currentSeasonId}
        currentSeasonName={currentSeasonName}
      />

      {/* Notifications */}
      {showNotifs && (
        <NotifSheet
          notifications={notifications}
          onClose={() => setShowNotifs(false)}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
        />
      )}

      {/* Apr 2026 — Persistent 4-tab bottom nav (real-mobile-app feel).
          Tabs: Home | Schedule | Chat | More. The "More" tab opens
          the side drawer which has all the rest (Players, Seasons,
          Stats, Finance, Notification Log, Team Settings, Profile,
          Notifications, Logout). Hidden in landscape on small
          screens via the .md:hidden tailwind class on the nav root.

          NOTE: this REPLACES the previous "drawer-only" pattern. The
          hamburger button in the top bar still works (and opens the
          same drawer), but most users will tap "More" instead since
          it's always visible at the bottom. */}
      <BottomNav
        activeTeam={activeTeam}
        unreadCount={unreadCount}
        unreadNotifs={unreadNotifs}
        onMoreClick={() => setDrawerOpen(true)}
      />
    </div>
  );
}
